import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../App";
import api from "../../api/axios";
import { toast } from "sonner";
import { Power, Phone, Clock, LogOut } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const AgentDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchCallHistory();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/agents/me');
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchCallHistory = async () => {
    try {
      const response = await api.get('/calls/history');
      setCallHistory(response.data);
    } catch (error) {
      toast.error("Failed to load call history");
    }
  };

  const toggleStatus = async () => {
    const newStatus = profile.status === "Available" ? "Offline" : "Available";
    try {
      await api.put('/agents/status', { status: newStatus });
      setProfile({ ...profile, status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="PassportTalk logo" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-travel-navy font-outfit" data-testid="agent-dashboard-title">PassportTalk</h1>
              <p className="text-sm text-slate-600">Agent Dashboard</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full px-6 py-3 font-medium transition-colors flex items-center gap-2"
            data-testid="agent-logout-button"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>

        {profile && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-aviation to-sky-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {profile.display_name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-ocean" data-testid="agent-display-name">{profile.display_name}</h2>
                  <p className="text-slate-600">{user?.email}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-sage/30 text-ocean text-xs font-medium rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    profile.status === "Available"
                      ? "bg-green-100 text-green-700"
                      : profile.status === "Busy"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-slate-100 text-slate-700"
                  }`}
                  data-testid="agent-status"
                >
                  {profile.status}
                </span>
              </div>
            </div>

            <button
              onClick={toggleStatus}
              disabled={profile.status === "Busy"}
              className={`w-full h-12 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                profile.status === "Available"
                  ? "bg-slate-200 hover:bg-slate-300 text-slate-700"
                  : "bg-green-500 hover:bg-green-600 text-white shadow-lg"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="toggle-status-button"
            >
              <Power className="w-5 h-5" />
              {profile.status === "Available" ? "Go Offline" : "Go Online"}
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ocean mb-4">Recent Calls</h3>
          {callHistory.length === 0 ? (
            <p className="text-slate-600" data-testid="no-calls-message">No calls yet</p>
          ) : (
            <div className="space-y-3">
              {callHistory.map((call) => (
                <div
                  key={call.id}
                  className="flex items-start justify-between p-4 bg-slate-50 rounded-xl border border-slate-100"
                  data-testid={`agent-call-${call.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="w-4 h-4 text-aviation" />
                      <p className="font-medium text-ocean">{call.traveler_name}</p>
                    </div>
                    <p className="text-sm text-slate-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(call.start_time)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">{formatDuration(call.duration_seconds)}</p>
                    {call.billed_amount && (
                      <p className="text-sm font-semibold text-ocean">${call.billed_amount.toFixed(2)}</p>
                    )}
                    {call.rating && (
                      <p className="text-sm text-yellow-600">★ {call.rating}/5</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
